import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import Marquee from "../components/Marquee";
import { ArrowRight, Leaf, MapPin, Shield } from "lucide-react";

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data));
  }, []);

  return (
    <div data-testid="home-page" className="pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7 fade-up">
            <p className="text-xs uppercase tracking-overline text-[#4A5D23] mb-6">
              EarthOrg Ventures LLP — Est. India
            </p>
            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter">
              Crimson threads.<br/>
              Wild forest jars.<br/>
              <span className="italic text-[#C84B31]">Heirloom spice.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-[#4A4A4A] leading-relaxed">
              We source the finest saffron, raw honey and heritage spices directly
              from Indian farms and forests — and ship them, traceable and lab-tested,
              to gourmet kitchens around the world.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                data-testid="hero-shop-cta"
                to="/shop"
                className="bg-[#C84B31] text-[#FAF9F6] px-8 py-4 hover:bg-[#A93F29] transition-colors uppercase tracking-overline text-sm inline-flex items-center gap-3"
              >
                Shop the Cellar <ArrowRight size={16} />
              </Link>
              <Link
                data-testid="hero-wholesale-cta"
                to="/wholesale"
                className="bg-transparent border border-[#1A1A1A] text-[#1A1A1A] px-8 py-4 hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors uppercase tracking-overline text-sm inline-flex items-center gap-3"
              >
                Bulk & Wholesale
              </Link>
            </div>
          </div>
          <div className="md:col-span-5 fade-up-delay-1">
            <div className="relative aspect-[4/5] bg-[#EAE6DB] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/10487658/pexels-photo-10487658.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Saffron threads"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="mt-4 text-xs uppercase tracking-overline text-[#7A7A7A]">
              ★ Lot 042 · Pampore Mongra · Harvest Nov 2025
            </p>
          </div>
        </div>
      </section>

      <Marquee />

      {/* STORY BENTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-5 md:sticky md:top-28 self-start">
            <p className="text-xs uppercase tracking-overline text-[#4A5D23] mb-4">A House of Origin</p>
            <h2 className="font-serif-display text-5xl md:text-6xl tracking-tight leading-[1.02]">
              We don&apos;t<br/>resell.<br/>
              <em className="text-[#C84B31] not-italic font-light">
                We harvest.
              </em>
            </h2>
            <p className="mt-8 text-[#4A4A4A] leading-relaxed text-lg">
              Each lot is hand-picked by farmers we know by name. Cold-stored,
              lab-tested, then flown to your port. No middlemen. No blends.
              Just the real thing, from the soil that grew it.
            </p>
          </div>

          <div className="md:col-span-7 grid grid-cols-2 gap-6">
            <div className="aspect-[4/5] overflow-hidden bg-[#EAE6DB]">
              <img src="https://images.pexels.com/photos/17870116/pexels-photo-17870116.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720" alt="Spice market" className="w-full h-full object-cover" />
            </div>
            <div className="bg-[#F5F2EA] border border-[#D5D1C5] p-8 flex flex-col justify-between">
              <Leaf size={28} strokeWidth={1.5} className="text-[#4A5D23]" />
              <div>
                <h3 className="font-serif-display text-3xl tracking-tight">Single-Origin</h3>
                <p className="text-sm text-[#4A4A4A] mt-3">Every jar names the village, the farmer and the harvest month.</p>
              </div>
            </div>
            <div className="bg-[#1A1A1A] text-[#FAF9F6] p-8 flex flex-col justify-between">
              <MapPin size={28} strokeWidth={1.5} className="text-[#C84B31]" />
              <div>
                <h3 className="font-serif-display text-3xl tracking-tight">Traceable</h3>
                <p className="text-sm text-[#D5D1C5] mt-3">Farm-to-port chain of custody on every shipment. Export-cleared.</p>
              </div>
            </div>
            <div className="aspect-[4/5] overflow-hidden bg-[#EAE6DB]">
              <img src="https://images.pexels.com/photos/13246534/pexels-photo-13246534.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720" alt="Raw honey" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="text-xs uppercase tracking-overline text-[#4A5D23] mb-4">The Cellar</p>
            <h2 className="font-serif-display text-5xl md:text-6xl tracking-tight">This Season&apos;s Pick</h2>
          </div>
          <Link data-testid="see-all-products" to="/shop" className="hidden md:inline-flex text-xs uppercase tracking-overline text-[#C84B31] hover:text-[#A93F29] items-center gap-2 underline underline-offset-4">
            See all <ArrowRight size={14}/>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      {/* CERTIFICATION STRIP */}
      <section className="bg-[#F5F2EA] border-y border-[#D5D1C5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { icon: Shield, h: "Lab Tested", p: "ISO 3632 grading, heavy-metal & MRL panels on every lot." },
            { icon: Leaf, h: "Organic Where it Counts", p: "USDA & India Organic certified honey and select spice lots." },
            { icon: MapPin, h: "Export-Ready", p: "Vacuum or N₂-flushed packaging, customs paperwork included." },
          ].map((it, i) => (
            <div key={i} className="border-t border-[#1A1A1A] pt-8">
              <it.icon size={28} strokeWidth={1.5} className="text-[#C84B31]" />
              <h3 className="font-serif-display text-3xl mt-6 tracking-tight">{it.h}</h3>
              <p className="text-sm text-[#4A4A4A] mt-3 leading-relaxed">{it.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
        <p className="text-xs uppercase tracking-overline text-[#4A5D23] mb-6">For Importers, Hotels & Distributors</p>
        <h2 className="font-serif-display text-5xl md:text-7xl tracking-tighter leading-none max-w-4xl mx-auto">
          Order a container, or a kilo.<br/>
          <em className="text-[#C84B31] not-italic">We ship both.</em>
        </h2>
        <Link
          data-testid="cta-wholesale"
          to="/wholesale"
          className="inline-flex mt-12 bg-[#1A1A1A] text-[#FAF9F6] px-10 py-5 hover:bg-[#C84B31] transition-colors uppercase tracking-overline text-sm items-center gap-3"
        >
          Request a Wholesale Quote <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
}
